/**
 * Settings › Customers › **Texts & email** — `docs/activity.md` §12.3,
 * Board 17 E.
 *
 * Where a customer's message lands — the three steps of §7.4, read-only,
 * because routing is a rule rather than a setting — and who watches each
 * branch's **Unassigned** (a role in a branch, or a person; they get the bell
 * for each new one). Then the company's text number (bought by Koowali, so
 * **Change** asks Rowted rather than changing it — §19 A4), what an email from
 * Rowted says it is from, and the two Outlook switches. Everything saves at
 * once.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { can, formatPhone, type MeResponse, type PeopleDirectory } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { Button, Switch } from '../components/primitives.js'
import { Modal, Popover } from '../components/overlays.js'
import { TextArea } from '../components/form/index.js'
import { Glyph } from '../activity/glyphs.js'
import { useSettingsBar } from './settings-bar.js'

interface Watcher {
  id: string
  orgUnitId: string
  branch: string
  roleId: string | null
  roleName: string | null
  userId: string | null
  userName: string | null
}

interface TextsAndEmail {
  smsNumber: string | null
  outlookAllowed: boolean
  outlookContactsOnly: boolean
  mailbox: string | null
  replyAddress: string | null
  repliesLine: string | null
  sentAs: string | null
  watchers: Watcher[]
  branches: { id: string; name: string }[]
}

/** §7.4's order, as drawn. */
const STEPS: [string, string][] = [
  ['The last person who wrote to them', 'by text or email, from Rowted or their Outlook'],
  ['The salesperson on their open job', 'the newest open job at their property'],
  ['Unassigned for their branch', 'its watchers and the branch’s salespeople can take it'],
]

export function TextsAndEmailPage({ me }: { me: MeResponse }) {
  const canEdit = can(me.permissions, 'company_settings', 'edit')
  const [data, setData] = useState<TextsAndEmail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [asking, setAsking] = useState(false)
  const addAt = useRef<HTMLButtonElement>(null)

  const bar = useSettingsBar({ title: 'Texts & email', sub: 'where customer messages land, and how email is sent' })

  const load = useCallback(async () => {
    try {
      setData(await request<TextsAndEmail>('/settings/texts-and-email'))
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'These settings did not load.')
    }
  }, [])
  useEffect(() => { void load() }, [load])

  const patch = async (body: { outlookAllowed?: boolean; outlookContactsOnly?: boolean }) => {
    if (!data) return
    setError(null)
    setData({ ...data, ...body })
    try {
      setData(await request<TextsAndEmail>('/settings/texts-and-email', { method: 'PATCH', body }))
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'That did not save.')
      void load()
    }
  }

  const removeWatcher = async (watcher: Watcher) => {
    if (!data) return
    try {
      const { items } = await request<{ items: Watcher[] }>(`/settings/texts-and-email/watchers/${watcher.id}/remove`,
        { method: 'POST', body: {} })
      setData({ ...data, watchers: items })
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'That did not save.')
    }
  }

  /*
   * The branches the header is looking at (Board 17 E: *Harrisburg district*
   * shows Harrisburg's watchers). A person's chip names its branch only when
   * more than one is in view; a role's always does — it is a role *there*.
   */
  const inView = new Set(me.scope.selected)
  const branches = (data?.branches ?? []).filter((branch) => inView.has(branch.id))
  const shown = (data?.watchers ?? []).filter((watcher) => inView.has(watcher.orgUnitId))
  const manyBranches = branches.length > 1
  const chipOf = (watcher: Watcher) => (watcher.roleName
    ? `${watcher.roleName} · ${watcher.branch}`
    : manyBranches ? `${watcher.userName ?? ''} · ${watcher.branch}` : watcher.userName ?? '')

  return (
    <div className="px-6 py-5">
      {bar}
      {!data ? (
        <p className="m-0 text-[13px] text-text-muted">{error ?? 'Loading…'}</p>
      ) : (
        <div className="flex flex-wrap items-start gap-10">
          <div className="flex min-w-0 max-w-[440px] flex-[1_1_400px] flex-col">
            <b className="text-[14px]">When a customer texts or emails</b>
            <div className="mb-3 mt-0.5 text-[12px] text-text-muted">it goes to the first of these that applies</div>
            {STEPS.map(([title, sub], index) => (
              <div key={title} className="flex flex-col">
                {index > 0 && <div aria-hidden className="ml-[25px] h-3.5 border-l-2 border-dotted border-line-strong" />}
                <div className="flex h-14 items-center gap-3 rounded-card border border-line bg-surface px-3.5">
                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[12px] font-bold text-accent-dark">
                    {index + 1}
                  </span>
                  <div className="flex min-w-0 flex-col">
                    <b className="text-[13px]">{title}</b>
                    <span className="text-[11.5px] text-text-muted">{sub}</span>
                  </div>
                </div>
              </div>
            ))}

            <div className="mb-[5px] mt-4 text-[11.5px] font-medium text-text-muted">Who watches Unassigned</div>
            <div className="flex flex-wrap gap-1.5 rounded-button border border-line bg-surface p-1.5">
              {shown.map((watcher) => (
                <span key={watcher.id}
                  className="inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-full border border-accent-line bg-accent-soft pl-2.5 pr-1.5 text-[12.5px] font-semibold text-accent-dark">
                  {chipOf(watcher)}
                  {canEdit && (
                    <button type="button" aria-label={`Stop ${chipOf(watcher)} watching`}
                      onClick={() => void removeWatcher(watcher)} className="inline-flex">
                      <Glyph name="close" size={12} stroke={2.4} />
                    </button>
                  )}
                </span>
              ))}
              {canEdit && (
                <button ref={addAt} type="button" onClick={() => setAdding(true)}
                  className="inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-full border border-dashed border-accent-line px-3 text-[12.5px] font-semibold text-accent">
                  <Glyph name="plus" size={13} />Add
                </button>
              )}
            </div>
            <div className="mt-1 text-[11px] leading-[1.4] text-text-faint">they get the bell for each new one</div>
          </div>

          <div className="flex min-w-0 max-w-[460px] flex-[1_1_420px] flex-col">
            <b className="text-[14px]">Texts</b>
            <div className="mt-2.5 flex items-center gap-2.5 rounded-card border border-line bg-surface px-3.5 py-3">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-tile border border-teal-band-line bg-teal-band-bg text-teal-band-text">
                <Glyph name="bubble" size={16} />
              </span>
              <div className="flex min-w-0 flex-col">
                <b className="text-[13.5px]">{data.smsNumber ? formatPhone(data.smsNumber) : 'No number yet'}</b>
                <span className="text-[11.5px] text-text-muted">the company’s number · every texter sends from it</span>
              </div>
              <span className="flex-1" />
              {canEdit && <Button className="h-[30px] text-[12px]" onClick={() => setAsking(true)}>Change</Button>}
            </div>

            <b className="mt-[18px] text-[14px]">Email</b>
            <div className="mt-2.5 rounded-card border border-line bg-surface px-3.5 py-3 text-[12.5px] leading-[1.6] text-text-body">
              <b>From Rowted</b> — sent as <i>{data.sentAs ?? 'you'}</i>; replies go to a private address for each
              conversation and come back into the Inbox.
            </div>
            {data.repliesLine && (
              <div className="mt-2 text-[12px] font-semibold text-warn">{data.repliesLine}</div>
            )}
            <div className="mt-2.5 flex items-center gap-2.5 text-[13px]">
              <Switch label="Let people connect their own Outlook" hideLabel checked={data.outlookAllowed}
                disabled={!canEdit} onChange={(outlookAllowed) => void patch({ outlookAllowed })} />
              Let people connect their own Outlook
              <span className="text-[11.5px] text-text-faint">My account → Email</span>
            </div>
            <div className="mt-2.5 flex items-center gap-2.5 text-[13px]">
              <Switch label="Bring in replies only from contacts" hideLabel checked={data.outlookContactsOnly}
                disabled={!canEdit} onChange={(outlookContactsOnly) => void patch({ outlookContactsOnly })} />
              Bring in replies only from contacts
              <span className="text-[11.5px] text-text-faint">Outlook mail from anyone else stays out</span>
            </div>
            {error && <p role="alert" className="m-0 mt-3 text-[12.5px] text-danger">{error}</p>}
          </div>
        </div>
      )}

      {adding && data && (
        <AddWatcher anchor={addAt.current} branches={branches}
          onClose={() => setAdding(false)}
          onAdded={(watchers) => { setAdding(false); setData({ ...data, watchers }) }} />
      )}
      {asking && <AskForNumber number={data?.smsNumber ?? null} onClose={() => setAsking(false)} />}
    </div>
  )
}

interface WatcherChoice { kind: 'role' | 'person'; id: string; label: string; sub: string }

/** **+ Add**: a role in a branch, or a person. */
function AddWatcher({ anchor, branches, onClose, onAdded }: {
  anchor: HTMLElement | null
  branches: { id: string; name: string }[]
  onClose: () => void
  onAdded: (watchers: Watcher[]) => void
}) {
  const [branchId, setBranchId] = useState(branches[0]?.id ?? '')
  const [choices, setChoices] = useState<WatcherChoice[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    void Promise.all([
      request<{ items: { id: string; name: string; defaultApp: string; people: string[] }[] }>('/roles/activity')
        .then((answer) => answer.items).catch(() => []),
      request<PeopleDirectory>('/activity/people').then((answer) => answer.people).catch(() => []),
    ]).then(([roles, people]) => {
      if (!live) return
      setChoices([
        ...roles.filter((role) => role.defaultApp !== 'crew_app')
          .map((role) => ({ kind: 'role' as const, id: role.id, label: role.name,
            sub: `${role.people.length} ${role.people.length === 1 ? 'person' : 'people'}` })),
        ...people.map((person) => ({ kind: 'person' as const, id: person.id, label: person.name,
          sub: person.role ?? '' })),
      ])
    })
    return () => { live = false }
  }, [])

  const add = async (choice: WatcherChoice) => {
    setError(null)
    try {
      const { items } = await request<{ items: Watcher[] }>('/settings/texts-and-email/watchers', {
        method: 'POST',
        body: { orgUnitId: branchId, ...(choice.kind === 'role' ? { roleId: choice.id } : { userId: choice.id }) },
      })
      onAdded(items)
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'That did not save.')
    }
  }

  return (
    <Popover open onClose={onClose} anchor={anchor} width={300} label="Who watches Unassigned" maxHeight={420}
      className="rounded-[12px] py-1">
      {branches.length > 1 && (
        <div className="flex flex-wrap gap-1 border-b border-line-faint px-3 pb-2 pt-1.5">
          {branches.map((branch) => (
            <button key={branch.id} type="button" aria-pressed={branch.id === branchId}
              onClick={() => setBranchId(branch.id)}
              className={cn('h-7 rounded-full border px-2.5 text-[12px] font-semibold',
                branch.id === branchId ? 'border-accent-line bg-accent-soft text-accent-dark' : 'border-line text-text-muted')}>
              {branch.name}
            </button>
          ))}
        </div>
      )}
      <div role="menu" aria-label="Who watches Unassigned" className="flex flex-col overflow-y-auto">
        {choices === null && <span className="px-3 py-2 text-[12.5px] text-text-muted">Loading…</span>}
        {(['role', 'person'] as const).map((kind) => {
          const rows = (choices ?? []).filter((choice) => choice.kind === kind)
          if (rows.length === 0) return null
          return (
            <div key={kind} className="flex flex-col">
              <p className="m-0 px-3 pb-0.5 pt-1.5 text-label uppercase text-text-faint">{kind === 'role' ? 'Roles' : 'People'}</p>
              {rows.map((choice) => (
                <button key={choice.id} type="button" role="menuitem" onClick={() => void add(choice)}
                  className="mx-1 flex flex-col rounded-[9px] px-3 py-1.5 text-left hover:bg-hover">
                  <b className="text-[13px]">{choice.label}</b>
                  {choice.sub && <span className="text-[11.5px] text-text-muted">{choice.sub}</span>}
                </button>
              ))}
            </div>
          )
        })}
      </div>
      {error && <p role="alert" className="m-0 px-3 py-2 text-[12px] text-danger">{error}</p>}
    </Popover>
  )
}

/** *Ask Rowted to change your number* (§19 A4): a note to support, never a self-service change. */
function AskForNumber({ number, onClose }: { number: string | null; onClose: () => void }) {
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState<boolean | null>(null)

  const send = async () => {
    setBusy(true)
    try {
      const answer = await request<{ delivered: boolean }>('/settings/texts-and-email/number-request',
        { method: 'POST', body: { message } })
      setSent(answer.delivered)
    } catch {
      setSent(false)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      width={440}
      title="Ask Rowted to change your number"
      footer={sent === null ? (
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={busy} onClick={() => void send()}>
            {busy ? 'Sending…' : 'Send to Rowted'}
          </Button>
        </>
      ) : <Button onClick={onClose}>Done</Button>}
    >
      <div className="flex flex-col gap-3 p-4 text-[13px] leading-[1.45] text-text-body">
        {sent === null && (
          <>
            <p className="m-0">
              Rowted buys and sets up the number every texter sends from
              {number ? <> — now <b>{formatPhone(number)}</b></> : null}. Say what you need and we’ll change it.
            </p>
            <TextArea aria-label="What you need" value={message} onChange={(event) => setMessage(event.target.value)}
              placeholder="A local number for the Carlisle branch…" rows={3} />
          </>
        )}
        {sent === true && <p className="m-0">Sent. Rowted will reply to you by email.</p>}
        {sent === false && (
          <p className="m-0">That didn’t go just now. Email support@rowted.com and we’ll change it.</p>
        )}
      </div>
    </Modal>
  )
}
